import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useYacht } from "../../context/YachtContext";
import { api, fmtErr } from "../../lib/api";
import { PageHeader } from "./Dashboard";
import { Field, EmptyYacht } from "./Inventory";
import { Plus, Trash2, BookOpen, Wrench, LocateFixed, Loader2 } from "lucide-react";
import { AuditTimeline } from "../../components/AuditTimeline";
import { resolveUnitLocation } from "../../lib/engineLocations";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import ConfirmDialog from "../../components/ConfirmDialog";
import { toast } from "sonner";
import { useRights } from "../../lib/useRights";
import { useTour } from "../../components/tour/TourContext";
import TourReplayButton from "../../components/tour/TourReplayButton";

const LOG_TYPES = ["daily", "trip", "engine", "fuel", "generator", "guest", "incident", "weather", "crew"];

// "39.5665°N, 2.6358°E" — standard nautical-ish display of a GPS fix.
const fmtCoords = (lat, lon) =>
  `${Math.abs(lat).toFixed(4)}°${lat >= 0 ? "N" : "S"}, ${Math.abs(lon).toFixed(4)}°${lon >= 0 ? "E" : "W"}`;

// WMO weather codes → short human text (Open-Meteo `weather_code`).
const WMO_TEXT = {
  0: "Clear sky", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast",
  45: "Fog", 48: "Rime fog", 51: "Light drizzle", 53: "Drizzle", 55: "Heavy drizzle",
  56: "Freezing drizzle", 57: "Freezing drizzle", 61: "Light rain", 63: "Rain", 65: "Heavy rain",
  66: "Freezing rain", 67: "Freezing rain", 71: "Light snow", 73: "Snow", 75: "Heavy snow",
  77: "Snow grains", 80: "Light showers", 81: "Showers", 82: "Heavy showers",
  85: "Snow showers", 86: "Snow showers", 95: "Thunderstorm", 96: "Thunderstorm w/ hail", 99: "Thunderstorm w/ hail",
};
const compass = (deg) =>
  ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"][Math.round(deg / 22.5) % 16];
// Douglas sea scale descriptor from significant wave height (m).
const douglas = (h) =>
  h < 0.1 ? "Calm (glassy)" : h < 0.5 ? "Smooth" : h < 1.25 ? "Slight" : h < 2.5 ? "Moderate"
    : h < 4 ? "Rough" : h < 6 ? "Very rough" : h < 9 ? "High" : h < 14 ? "Very high" : "Phenomenal";

// Current weather + sea state for a position via Open-Meteo (free, no key).
// Marine wave data is unavailable inland / for some coasts — sea_state just
// stays empty then. Both calls are best-effort.
async function fetchConditions(lat, lon) {
  const out = { weather: "", sea_state: "" };
  try {
    const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code,wind_speed_10m,wind_direction_10m&wind_speed_unit=kn`);
    const c = (await r.json())?.current;
    if (c) {
      out.weather = [
        WMO_TEXT[c.weather_code],
        Number.isFinite(c.temperature_2m) ? `${Math.round(c.temperature_2m)}°C` : null,
        Number.isFinite(c.wind_speed_10m) ? `wind ${Math.round(c.wind_speed_10m)} kn ${compass(c.wind_direction_10m || 0)}` : null,
      ].filter(Boolean).join(", ");
    }
  } catch { /* offline — leave empty */ }
  try {
    const r = await fetch(`https://marine-api.open-meteo.com/v1/marine?latitude=${lat}&longitude=${lon}&current=wave_height`);
    const h = (await r.json())?.current?.wave_height;
    if (Number.isFinite(h)) out.sea_state = `${douglas(h)} — waves ${h.toFixed(1)} m`;
  } catch { /* inland / offline — leave empty */ }
  return out;
}

/* "Use current position" — the crew is aboard, so the device's GPS fix IS the
 * yacht's position. Reverse-geocodes via OpenStreetMap Nominatim (free, CORS-
 * enabled) to a readable place name; falls back to bare coordinates when the
 * lookup fails or returns nothing (open sea). */
function UseCurrentPosition({ onResolved }) {
  const [busy, setBusy] = useState(false);
  const capture = () => {
    if (!navigator.geolocation) {
      toast.error("This device doesn't support location access.");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const { latitude: lat, longitude: lon } = coords;
        const [place, conds] = await Promise.all([
          (async () => {
            try {
              const r = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=12&accept-language=en`,
                { headers: { Accept: "application/json" } },
              );
              const d = await r.json();
              const a = d?.address || {};
              return a.harbour || a.marina || a.village || a.town || a.city || a.municipality || a.island || d?.name || "";
            } catch { return ""; /* open sea / offline — coordinates alone are fine */ }
          })(),
          fetchConditions(lat, lon),
        ]);
        onResolved({
          location: place ? `${place} · ${fmtCoords(lat, lon)}` : fmtCoords(lat, lon),
          ...conds,
        });
        setBusy(false);
      },
      (err) => {
        setBusy(false);
        toast.error(err?.code === 1
          ? "Location permission denied — allow location access and try again."
          : "Couldn't get a GPS fix — try again outside or near a window.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
    );
  };
  return (
    <button
      type="button"
      onClick={capture}
      disabled={busy}
      className="inline-flex items-center gap-1 text-[11px] font-medium text-ink bg-white border border-zinc-300 rounded-md px-2 py-0.5 hover:bg-zinc-50 disabled:opacity-50 transition-colors"
      data-testid="log-use-position"
      title="Fill location, weather and sea state from the device's current GPS position"
    >
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <LocateFixed className="h-3 w-3" />}
      {busy ? "Locating…" : "Use current position & conditions"}
    </button>
  );
}

// Roles eligible to appear in the "Captain on duty" dropdown. Owners outrank
// captains in the rights matrix and can take command in a pinch; managers
// are the operational deputies. Engineers/deckhands/providers are
// deliberately excluded so the field stays a record of *who held command*,
// not just *who was on board*.
const COMMAND_ROLES = new Set(["owner", "manager", "captain", "first_officer"]);

const todayISO = () => new Date().toISOString().slice(0, 10);

const DEFAULT = {
  log_type: "daily", captain: "", log_date: todayISO(),
  location: "", weather: "", sea_state: "",
  fuel_level: "", generator_hours: "",
  departure: "", arrival: "", distance_nm: "", avg_speed: "", notes: "",
};

/**
 * Flatten the active yacht's engines (main + tender) into a single list of
 * { id, label, current_hours } so the form can render one hours input per
 * engine. Engines without an `id` are skipped — they predate the per-engine
 * identity feature and will be re-stamped on the next yacht save.
 */
function flatYachtEngines(yacht) {
  /**
   * Build the engine/generator/tender/stabilizer list rendered in the
   * Logbook trip form + entry detail. Per the 2026-05-31 spec, the
   * captain only sees the PHYSICAL LOCATION (Portside / Center /
   * Starboard) of each unit — no brand, no type, no serial, no
   * "Engine 1" fallback. When a unit has no explicit ``location`` we
   * derive one from its position in the array (see
   * ``defaultLocationLayout`` in lib/engineLocations.js).
   *
   * The ``kind_label`` is hinted on the row so the entry-detail view
   * can prepend "Engine", "Generator", etc. without re-reading the
   * yacht doc.
   */
  const out = [];
  const pushUnits = (units, kind) => {
    const list = (units || []).filter((u) => u && u.id);
    list.forEach((unit, idx) => {
      out.push({
        id: unit.id,
        label: resolveUnitLocation(unit, idx, list.length) || kind,
        kind,
        current_hours: unit.hours ?? null,
      });
    });
  };
  pushUnits(yacht?.engines, "Engine");
  pushUnits(yacht?.generators, "Generator");
  pushUnits(yacht?.stabilizers, "Stabilizer");
  (yacht?.tenders || []).forEach((t) => {
    const tEngines = (t.engines || []).filter((e) => e && e.id);
    tEngines.forEach((e, idx) => {
      // Tenders rarely have a "Portside / Center / Starboard" position,
      // so we let the tender name carry the identity and fall back to
      // the resolved location.
      const loc = resolveUnitLocation(e, idx, tEngines.length);
      const label = loc
        ? `${t.name || "Tender"} · ${loc}`
        : t.name || "Tender";
      out.push({ id: e.id, label, kind: "Tender", current_hours: e.hours ?? null });
    });
  });
  return out;
}

export default function Logbook() {
  const { t } = useTranslation();
  const { activeTour } = useTour();
  const { activeYacht, refresh: refreshYachts } = useYacht();
  const { can } = useRights();
  const [items, setItems] = useState([]);
  // Form modal state — `editing` carries the entry being edited, or the
  // string "new" for the create flow, or null when the modal is closed.
  // Collapsing the two flows lets us reuse one form definition + one
  // engine-readings panel without duplicating 60 lines of JSX.
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DEFAULT);
  const [engineReadings, setEngineReadings] = useState({}); // { engine_id: "512" }
  const [spawnedRoutines, setSpawnedRoutines] = useState(null);     // info dialog: auto-created jobs
  // Yacht crew filtered to owner/manager/captain — populates the
  // "Captain on duty" dropdown so the field stops being a free-text guess.
  const [commandCrew, setCommandCrew] = useState([]);
  // Entry being viewed in the read-only details modal.
  const [detail, setDetail] = useState(null);
  // Delete-confirmation gate so an accidental tap doesn't erase a trip log.
  const [toDelete, setToDelete] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  // ?open=<log_id> deep-link from the dashboard's Recent logbook panel —
  // opens the matching entry's detail dialog once the list loads, then
  // strips the query param so a reload doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const oid = searchParams.get("open");
    if (!oid || items.length === 0) return;
    const e = items.find((it) => it.id === oid);
    if (e) {
      setDetail(e);
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [items, searchParams, setSearchParams]);

  const load = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/logbook?yacht_id=${activeYacht.id}`);
      setItems(data);
    } catch { setItems([]); }
  };

  const loadCommandCrew = async () => {
    if (!activeYacht?.id) return;
    try {
      const { data } = await api.get(`/yachts/${activeYacht.id}/members`);
      // Keep only senior, *confirmed* crew. Pending invitees and junior
      // roles (engineer, deckhand, accountant, provider) never had the
      // authority to be "on duty", so they don't belong in the dropdown.
      setCommandCrew(
        (data || []).filter((m) => !m.pending && COMMAND_ROLES.has((m.role || "").toLowerCase())),
      );
    } catch { setCommandCrew([]); }
  };

  useEffect(() => {
    setItems([]);
    setCommandCrew([]);
    setDetail(null);
    load();
    loadCommandCrew();
    // eslint-disable-next-line
  }, [activeYacht?.id]);

  // Pre-fill the engine readings inputs with each engine's last-known hours
  // — but ONLY the moment the dialog opens (or when the user switches to a
  // different yacht while it is open). The YachtContext polls every 20s and
  // hands us a fresh `activeYacht` reference each tick; if we depended on
  // that reference, the user's in-progress edits would be silently
  // overwritten every poll. Depending on the yacht id alone fixes this —
  // the id only changes on a real switch, never on a context refresh.
  //
  // We also fetch a fresh copy of the yacht doc on dialog-open so the
  // engine list always reflects the latest persisted state (engines /
  // generators / stabilizers / tender engines + group labels). This
  // protects against a stale YachtContext where the user added a new
  // engine in another tab.
  useEffect(() => {
    if (!editing || !activeYacht?.id) return;
    let alive = true;
    (async () => {
      let yacht = activeYacht;
      try {
        const { data } = await api.get(`/yachts/${activeYacht.id}`);
        if (data) yacht = data;
      } catch { /* fall back to context */ }
      if (!alive) return;
      const engines = flatYachtEngines(yacht);
      const initial = {};
      engines.forEach((e) => {
        // In edit mode prefill from the entry's existing readings (so the
        // captain edits what was logged, not what's "current"). In create
        // mode default to the engine's current high-water-mark hours.
        if (editing && typeof editing === "object") {
          const r = (editing.engine_readings || []).find((rr) => rr.engine_id === e.id);
          initial[e.id] = r ? String(r.hours) : "";
        } else {
          initial[e.id] = e.current_hours == null ? "" : String(e.current_hours);
        }
      });
      setEngineReadings(initial);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, activeYacht?.id]);

  const save = async () => {
    try {
      const payload = { ...form, yacht_id: activeYacht.id, photos: [] };
      // ``generator_hours`` is dropped from the form as of 2026-06-11
      // (collected per-unit in the Engine hours panel instead). We
      // keep the field on the payload so older logbook entries with a
      // stored value can still be edited without losing it, but new
      // entries always send ``null`` — no double-entry.
      payload.generator_hours = null;
      ["fuel_level", "distance_nm", "avg_speed"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : Number(payload[k]);
      });
      // Build per-engine readings array — only include engines whose input
      // was changed from blank to a number. Empty string = "no new reading
      // for this engine" (skip), which lets the captain log a non-engine
      // entry (e.g. fuel-only) without forcing engine hours.
      const readings = [];
      Object.entries(engineReadings).forEach(([engine_id, val]) => {
        if (val === "" || val == null) return;
        const num = Number(val);
        if (!Number.isFinite(num)) return;
        readings.push({ engine_id, hours: num });
      });
      payload.engine_readings = readings;
      const isEdit = editing && typeof editing === "object";
      const { data } = isEdit
        ? await api.patch(`/logbook/${editing.id}`, payload)
        : await api.post("/logbook", payload);
      toast.success(isEdit ? "Log entry updated" : "Log entry added");
      setForm(DEFAULT); setEngineReadings({}); setEditing(null); load();
      // Engine-hours routines that just crossed their target are AUTO-SPAWNED
      // server-side (same as the calendar scheduler / manual "Trigger now").
      // We just surface what was created so the user can jump to it.
      if (!isEdit && Array.isArray(data?.spawned_jobs) && data.spawned_jobs.length > 0) {
        setSpawnedRoutines(data.spawned_jobs);
        const n = data.spawned_jobs.length;
        toast.success(
          `${n} periodic task${n === 1 ? "" : "s"} triggered — job${n === 1 ? "" : "s"} created in Tasks triggered.`,
        );
      }
      // Yacht engine hours likely changed — refresh the cached yacht so
      // routine "currently X h" displays stay in sync.
      try { await refreshYachts?.(); } catch { /* ignore */ }
    } catch (e) { toast.error(fmtErr(e)); }
  };

  // Opens the form in edit mode, prefilled from the existing entry. The
  // engine readings panel is hydrated separately by the same effect that
  // hydrates create mode (it now branches on `editing`'s shape).
  const startEdit = (entry) => {
    setDetail(null);
    setForm({
      log_type: entry.log_type || "daily",
      captain: entry.captain || "",
      // Hydrate log_date — prefer the explicit field; fall back to the
      // creation timestamp's date so older entries are still editable
      // without losing context. Slice grabs the ISO-8601 YYYY-MM-DD
      // prefix straight from the stored timestamp.
      log_date: (entry.log_date || entry.created_at || todayISO()).slice(0, 10),
      location: entry.location || "",
      weather: entry.weather || "",
      sea_state: entry.sea_state || "",
      fuel_level: entry.fuel_level == null ? "" : String(entry.fuel_level),
      generator_hours: entry.generator_hours == null ? "" : String(entry.generator_hours),
      departure: entry.departure || "",
      arrival: entry.arrival || "",
      distance_nm: entry.distance_nm == null ? "" : String(entry.distance_nm),
      avg_speed: entry.avg_speed == null ? "" : String(entry.avg_speed),
      notes: entry.notes || "",
    });
    setEditing(entry);
  };

  const performDelete = async () => {
    if (!toDelete) return;
    setDelBusy(true);
    try {
      await api.delete(`/logbook/${toDelete.id}`);
      toast.success("Log entry deleted.");
      setToDelete(null);
      setDetail(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setDelBusy(false);
    }
  };

  if (!activeYacht) return <EmptyYacht />;

  const engines = flatYachtEngines(activeYacht);

  return (
    <div className="p-6 lg:p-10 max-w-7xl">
      <PageHeader
        overline={activeYacht.name}
        title={t("logbook.title")}
        right={
          can("logbook:create") ? (
          <div className="flex items-center gap-2">
            <TourReplayButton tourId="logbook-entry" />
          <Dialog
            open={!!editing}
            onOpenChange={(o) => { if (!o) { setEditing(null); setForm(DEFAULT); setEngineReadings({}); } }}
          >
            <DialogTrigger asChild>
              <Button
                className="bg-ink hover:bg-black"
                data-testid="add-log-btn"
                onClick={() => { setForm(DEFAULT); setEditing("new"); }}
              >
                <Plus className="h-4 w-4 mr-2" />{t("logbook.new_entry")}
              </Button>
            </DialogTrigger>
            <DialogContent
              className="w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto p-4 sm:p-6"
              onPointerDownOutside={(e) => { if (activeTour) e.preventDefault(); }}
              onInteractOutside={(e) => { if (activeTour) e.preventDefault(); }}
              onEscapeKeyDown={(e) => { if (activeTour) e.preventDefault(); }}
              onOpenAutoFocus={(e) => { if (activeTour) e.preventDefault(); }}
            >
              <DialogHeader>
                <DialogTitle>
                  {editing && typeof editing === "object" ? "Edit log entry" : t("logbook.new_entry")}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-tour="log-fields">
                <Field label="Log type">
                  <select
                    className="h-9 w-full rounded-md border border-input px-3 text-sm"
                    value={form.log_type}
                    onChange={(e) => setForm({ ...form, log_type: e.target.value })}
                    data-testid="log-type"
                  >
                    {LOG_TYPES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Captain on duty">
                  <select
                    className="h-9 w-full rounded-md border border-input px-3 text-sm"
                    value={form.captain}
                    onChange={(e) => setForm({ ...form, captain: e.target.value })}
                    data-testid="log-captain"
                  >
                    <option value="">— select —</option>
                    {commandCrew.map((m) => (
                      <option key={m.id} value={m.name || m.email}>
                        {(m.name || m.email)}{m.role ? ` · ${m.role}` : ""}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Date">
                  <Input
                    type="date"
                    value={form.log_date}
                    onChange={(e) => setForm({ ...form, log_date: e.target.value })}
                    data-testid="log-date"
                  />
                </Field>
                <Field label="Location">
                  <div className="space-y-1.5">
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="log-location" />
                    <UseCurrentPosition onResolved={({ location, weather, sea_state }) => setForm((f) => ({
                      ...f,
                      location,
                      weather: weather || f.weather,
                      sea_state: sea_state || f.sea_state,
                    }))} />
                  </div>
                </Field>
                <Field label="Weather"><Input value={form.weather} onChange={(e) => setForm({ ...form, weather: e.target.value })} data-testid="log-weather" /></Field>
                <Field label="Sea state"><Input value={form.sea_state} onChange={(e) => setForm({ ...form, sea_state: e.target.value })} data-testid="log-sea-state" /></Field>
                <Field label="Fuel level (%)"><Input type="number" value={form.fuel_level} onChange={(e) => setForm({ ...form, fuel_level: e.target.value })} /></Field>
                {form.log_type === "trip" && (
                  <>
                    <Field label="Departure">
                      <Input
                        type="time"
                        value={form.departure}
                        onChange={(e) => setForm({ ...form, departure: e.target.value })}
                        data-testid="log-departure"
                      />
                    </Field>
                    <Field label="Arrival">
                      <Input
                        type="time"
                        value={form.arrival}
                        onChange={(e) => setForm({ ...form, arrival: e.target.value })}
                        data-testid="log-arrival"
                      />
                    </Field>
                    <Field label="Distance (nm)"><Input type="number" value={form.distance_nm} onChange={(e) => setForm({ ...form, distance_nm: e.target.value })} /></Field>
                    <Field label="Avg speed (kn)"><Input type="number" value={form.avg_speed} onChange={(e) => setForm({ ...form, avg_speed: e.target.value })} /></Field>
                  </>
                )}
                <Field label="Notes" className="md:col-span-2"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="log-notes" /></Field>
              </div>

              {engines.length > 0 && (
                <div className="mt-4 rounded-md border border-zinc-200 bg-zinc-50/40 p-3" data-testid="engine-readings">
                  <div className="text-[11px] uppercase tracking-wider font-medium text-zinc-600 mb-2">
                    Engine hours
                  </div>
                  {(() => {
                    /* Group engine rows by their physical kind so a captain
                     * scans Main engines → Generators → Stabilizers →
                     * Tender engines top-down. Headings only render when
                     * a yacht actually has units of that kind, so a
                     * sailboat with no generators doesn't show an empty
                     * "Generators" label.
                     */
                    const SECTIONS = [
                      { kind: "Engine",     label: "Yacht engines" },
                      { kind: "Generator",  label: "Generators" },
                      { kind: "Stabilizer", label: "Stabilizers" },
                      { kind: "Tender",     label: "Tender engines" },
                    ];
                    return SECTIONS.map(({ kind, label }) => {
                      const rows = engines.filter((e) => e.kind === kind);
                      if (rows.length === 0) return null;
                      return (
                        <div key={kind} className="mb-3 last:mb-0" data-testid={`engine-readings-section-${kind.toLowerCase()}`}>
                          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">
                            {label}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {rows.map((e) => (
                              <Field key={e.id} label={e.label}>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={engineReadings[e.id] ?? ""}
                                  placeholder={e.current_hours == null ? "h" : `last: ${e.current_hours}h`}
                                  onChange={(ev) => setEngineReadings({ ...engineReadings, [e.id]: ev.target.value })}
                                  data-testid={`engine-reading-${e.id}`}
                                />
                              </Field>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                  <div className="text-[11px] text-zinc-500 mt-2">
                    Leave blank to skip an engine. Values lower than the last reading are ignored.
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { setEditing(null); setForm(DEFAULT); setEngineReadings({}); }}
                >
                  Cancel
                </Button>
                <Button onClick={save} className="bg-ink hover:bg-black" data-testid="save-log">
                  {editing && typeof editing === "object" ? "Save changes" : "Save entry"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
          ) : null
        }
      />

      <div className="mt-8 space-y-3" data-testid="log-list">
        {items.length === 0 && (
          <div className="rounded-md border border-zinc-200 bg-white p-10 text-center text-zinc-500">
            <BookOpen className="h-6 w-6 mx-auto text-zinc-300" />
            <div className="mt-2">No logbook entries yet.</div>
          </div>
        )}
        {items.map((it) => (
          <div
            key={it.id}
            className="rounded-md border border-zinc-200 bg-white p-4 cursor-pointer hover:border-zinc-300 active:bg-zinc-50 transition-colors"
            data-testid={`log-card-${it.id}`}
            onClick={() => setDetail(it)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetail(it); } }}
          >
            <div className="flex items-start justify-between gap-3 min-w-0">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-zinc-500">{it.log_type}</div>
                <div className="mt-1 font-medium break-words">{it.location || it.notes || "Entry"}</div>
                <div className="mt-1 text-xs text-zinc-500">
                  {(it.created_at || "").slice(0, 16).replace("T", " ")}
                  {it.created_by_name && (
                    <span className="ml-2 text-zinc-500">· logged by <span className="text-zinc-700">{it.created_by_name}</span></span>
                  )}
                  {it.captain && (
                    <span className="ml-2 text-zinc-500">· captain <span className="text-zinc-700">{it.captain}</span></span>
                  )}
                </div>
              </div>
              {/* Edited marker — small, unobtrusive; full audit lives in
                  the detail modal's History tab. */}
              {it.updated_at && (
                <span
                  className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded shrink-0"
                  data-testid={`log-edited-${it.id}`}
                >
                  edited
                </span>
              )}
            </div>
            {(it.weather || it.sea_state || it.engine_hours != null || (Array.isArray(it.engine_readings) && it.engine_readings.length > 0)) && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                {it.weather && <Pair k="Weather" v={it.weather} />}
                {it.sea_state && <Pair k="Sea state" v={it.sea_state} />}
                {it.fuel_level != null && <Pair k="Fuel" v={`${it.fuel_level}%`} />}
                {it.engine_hours != null && <Pair k="Engine hrs" v={it.engine_hours} />}
                {Array.isArray(it.engine_readings) && it.engine_readings.map((r) => {
                  const eng = engines.find((x) => x.id === r.engine_id);
                  return (
                    <Pair key={r.engine_id} k={eng?.label || "Engine"} v={`${r.hours} h`} />
                  );
                })}
                {it.distance_nm != null && <Pair k="Distance" v={`${it.distance_nm} nm`} />}
                {it.avg_speed != null && <Pair k="Speed" v={`${it.avg_speed} kn`} />}
              </div>
            )}
            {it.notes && <div className="mt-3 text-sm text-zinc-700">{it.notes}</div>}
          </div>
        ))}
      </div>

      <LogbookDetailDialog
        entry={detail}
        engines={engines}
        can={can}
        onClose={() => setDetail(null)}
        onEdit={() => startEdit(detail)}
        onDeleteRequest={() => setToDelete(detail)}
      />

      <ConfirmDialog
        open={!!toDelete}
        title="Delete logbook entry?"
        description={toDelete ? `This will permanently remove the ${toDelete.log_type || "log"} entry${toDelete.location ? ` for ${toDelete.location}` : ""}. The deletion is recorded in the audit history.` : ""}
        confirmLabel="Yes, delete"
        destructive
        busy={delBusy}
        onCancel={() => { if (!delBusy) setToDelete(null); }}
        onConfirm={performDelete}
      />

      {spawnedRoutines && (
        <SpawnedRoutinesDialog
          jobs={spawnedRoutines}
          onClose={() => setSpawnedRoutines(null)}
        />
      )}
    </div>
  );
}

function Pair({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
      <div className="text-sm">{v}</div>
    </div>
  );
}

// ─── Logbook entry details modal ─────────────────────────────────────────
// Read-only viewer triggered by tapping anywhere on a logbook row. Mirrors
// the Tasks JobDetailModal pattern — Details tab shows every field, History
// tab fetches /api/audit?ref_kind=logbook so a captain auditing an old trip
// can see who edited what and when. Footer carries Edit + Delete actions
// (Delete is double-confirmed by the parent's ConfirmDialog).
function LogbookDetailDialog({ entry, engines = [], can, onClose, onEdit, onDeleteRequest }) {
  const [tab, setTab] = useState("details");
  const [auditRows, setAuditRows] = useState(null);

  // Lazy-load history the first time the user opens the History tab — saves
  // a request per modal open for the common "I just want to read the entry"
  // case. Re-fetches whenever the entry changes (e.g. user switches rows).
  useEffect(() => {
    if (!entry) { setAuditRows(null); setTab("details"); return; }
    setAuditRows(null);
  }, [entry?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!entry || tab !== "history" || auditRows !== null) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/audit", { params: { ref_kind: "logbook", ref_id: entry.id } });
        if (alive) setAuditRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (alive) {
          toast.error(fmtErr(e));
          setAuditRows([]);  // stop the spinner; show empty state
        }
      }
    })();
    return () => { alive = false; };
  }, [entry?.id, tab, auditRows]);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!entry) return null;
  const created = (entry.created_at || "").replace("T", " ").slice(0, 16) || "—";
  const updated = (entry.updated_at || "").replace("T", " ").slice(0, 16);

  return (
    <Dialog open={!!entry} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[calc(100dvh-1rem)] overflow-y-auto p-4 sm:p-6"
        data-testid="log-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 flex-wrap">
            <span>Logbook entry</span>
            <span className="text-[10px] uppercase tracking-[0.2em] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">
              {entry.log_type}
            </span>
            {entry.updated_at && (
              <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded">
                edited
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-1">
          <TabsList className="w-full">
            <TabsTrigger value="details" data-testid="log-tab-details" className="flex-1">Details</TabsTrigger>
            <TabsTrigger value="history" data-testid="log-tab-history" className="flex-1">History</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <DetailRow label="Date">{created}</DetailRow>
              <DetailRow label="Logged by">{entry.created_by_name || "—"}</DetailRow>
              <DetailRow label="Captain on duty">{entry.captain || "—"}</DetailRow>
              <DetailRow label="Location">{entry.location || "—"}</DetailRow>
              <DetailRow label="Weather">{entry.weather || "—"}</DetailRow>
              <DetailRow label="Sea state">{entry.sea_state || "—"}</DetailRow>
              {entry.fuel_level != null && (
                <DetailRow label="Fuel level">{entry.fuel_level}%</DetailRow>
              )}
              {entry.generator_hours != null && (
                <DetailRow label="Generator hours">{entry.generator_hours} h</DetailRow>
              )}
              {entry.log_type === "trip" && (
                <>
                  <DetailRow label="Departure">{entry.departure || "—"}</DetailRow>
                  <DetailRow label="Arrival">{entry.arrival || "—"}</DetailRow>
                  {entry.distance_nm != null && <DetailRow label="Distance">{entry.distance_nm} nm</DetailRow>}
                  {entry.avg_speed != null && <DetailRow label="Avg speed">{entry.avg_speed} kn</DetailRow>}
                </>
              )}
              {updated && (
                <DetailRow label="Last edited">
                  {updated}
                  {entry.updated_by_name && <span className="text-zinc-500"> · by {entry.updated_by_name}</span>}
                </DetailRow>
              )}
            </div>

            {Array.isArray(entry.engine_readings) && entry.engine_readings.length > 0 && (
              <div className="mt-2 border-t border-zinc-200 pt-4">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Engine readings</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {entry.engine_readings.map((r) => {
                    const eng = engines.find((x) => x.id === r.engine_id);
                    return (
                      <div
                        key={r.engine_id}
                        className="flex items-center justify-between rounded-md border border-zinc-200 px-3 py-2"
                        data-testid={`log-detail-engine-${r.engine_id}`}
                      >
                        <span className="text-zinc-700">{eng?.label || "Engine"}</span>
                        <span className="font-medium">{r.hours} h</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {entry.notes && (
              <div className="mt-2 border-t border-zinc-200 pt-4">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Notes</div>
                <div className="text-sm text-zinc-800 whitespace-pre-wrap">{entry.notes}</div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <AuditTimeline rows={auditRows} emptyText="No edits yet. Future changes will appear here." testidPrefix="log-history" />
          </TabsContent>
        </Tabs>

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 mt-2">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto" data-testid="log-detail-close">Close</Button>
          {tab === "details" && (
            <div className="flex flex-col sm:flex-row gap-2">
              {can("logbook:delete") && (
                <Button
                  variant="outline"
                  className="w-full sm:w-auto text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={onDeleteRequest}
                  data-testid="log-detail-delete"
                >
                  <Trash2 className="h-4 w-4 mr-2" />Delete
                </Button>
              )}
              {can("logbook:edit") && (
                <Button
                  onClick={onEdit}
                  className="w-full sm:w-auto bg-ink hover:bg-black"
                  data-testid="log-detail-edit"
                >
                  Edit
                </Button>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="mt-1 text-zinc-900">{children}</div>
    </div>
  );
}

/**
 * Informational modal shown right after a logbook entry whose engine readings
 * crossed one or more routine-maintenance targets. The matching maintenance
 * jobs are AUTO-CREATED server-side (same as the calendar scheduler and the
 * manual "Trigger now" button) — this dialog simply confirms what was created
 * so the crew can jump to Maintenance → Periodic tasks → Triggered jobs.
 */
function SpawnedRoutinesDialog({ jobs, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="spawned-routines-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-emerald-600" />
            {jobs.length} maintenance {jobs.length === 1 ? "job" : "jobs"} created
          </DialogTitle>
        </DialogHeader>
        <div className="text-[12px] text-zinc-600">
          Your engine hours crossed the target for the following periodic task
          {jobs.length === 1 ? "" : "s"}, so the job{jobs.length === 1 ? " was" : "s were"} created
          automatically. Find {jobs.length === 1 ? "it" : "them"} under Maintenance → Periodic tasks →
          Tasks triggered. The periodic task stays and its next target has advanced.
        </div>
        <ul className="mt-2 divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white">
          {jobs.map((j) => (
            <li key={j.job_id} className="px-4 py-3 flex items-start gap-3" data-testid={`spawned-routine-${j.routine_id}`}>
              <Wrench className="h-4 w-4 text-zinc-500 mt-1 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{j.title}</div>
              </div>
              <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                Created
              </span>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button onClick={onClose} className="bg-ink hover:bg-black" data-testid="close-spawned-dialog">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
